import { useEffect, useState } from "react";
import React from "react";
import { api, formatApiError } from "@/lib/apiClient";
import LegacyCard from "@/components/LegacyCard";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Trophy, Award, TrendingUp, AlertTriangle, LineChart as LineIcon, Target, Star, Save, FileUp, Filter, Briefcase, Workflow, HeartHandshake, ExternalLink, Eye, EyeOff, Compass, Lock, Linkedin, Instagram, Facebook, Youtube, Share2, FileText, Camera, Users, ChevronDown, ChevronUp, Cake } from "lucide-react";
import CareerPanel from "@/components/CareerPanel";
import GPSPanel from "@/components/gps/GPSPanel";
import GPSYtdReport from "@/components/gps/GPSYtdReport";
import MyMentorsPanel from "@/components/gps/MyMentorsPanel";
import { MbtiBadge } from "@/lib/mbti";
import { PersonalEvolution } from "@/components/Charts";
import PhotoUpload from "@/components/PhotoUpload";
import MeetingCountdown from "@/components/MeetingCountdown";
import InvitePanel from "@/components/InvitePanel";
import FamilyTreePanel from "@/components/FamilyTreePanel";
import PushToggle from "@/components/PushToggle";
import InstallAppButton from "@/components/InstallAppButton";
import IdealPessoalPanel from "@/components/IdealPessoalPanel";
import CoachProfile from "@/pages/CoachProfile";
import StaffProfile from "@/pages/StaffProfile";
import { VIA_STRENGTHS, suggestedViaFor } from "@/lib/via";

export default function MyProfile() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [evo, setEvo] = useState(null);
  const isCoach = user?.role === "coach";
  const isStaff = user?.role === "staff";
  const load = () => {
    if (isCoach || isStaff) return;
    api.get("/my-stats").then((r) => setData(r.data));
    api.get("/evolution/me").then((r) => setEvo(r.data)).catch(() => setEvo({ weekly: [] }));
  };
  useEffect(() => { load(); }, [isCoach, isStaff]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isStaff) return <StaffProfile />;
  if (isCoach) return <CoachProfile />;

  if (!data) return <div className="kw-overline">A carregar…</div>;
  if (!data.player) {
    return (
      <div className="kw-card p-8 max-w-2xl">
        <AlertTriangle className="w-6 h-6 text-[#E50000] mb-3" />
        <h2 className="font-display text-2xl font-black uppercase">Sem registo de consultor</h2>
        <p className="text-[#A3A3A3] mt-2">
          O teu utilizador ainda não está associado a um consultor da liga. Pede ao admin para te adicionar
          como consultor usando o mesmo email da tua conta.
        </p>
      </div>
    );
  }

  const { player, stats, entries } = data;

  return (
    <div className="space-y-8 kw-fade-up" data-testid="profile-page">
      <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
        <PhotoUpload playerId={player.id} photoUrl={player.photo_url} name={player.name} size="xl" onChange={load} />
        <div className="flex-1">
          <div className="kw-overline mb-2">{player.team_name || "Sem equipa"}</div>
          <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">
            {player.name}
          </h1>
          {player.mbti && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <MbtiBadge mbti={player.mbti} label={player.personality_label} />
              <MbtiBadge club={player.club} />
              {player.tenure_years > 0 && (
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#E50000] text-[#E50000] text-[10px] uppercase tracking-widest font-bold"
                  title={player.joined_at ? `Na KW Lead desde ${player.joined_at}` : "Anos na KW Lead"}
                  data-testid="tenure-badge"
                >
                  <Cake className="w-3 h-3" /> {player.tenure_years} {player.tenure_years === 1 ? "ano" : "anos"} na KW Lead
                </span>
              )}
              {player.league_status === "bench" && (
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#222] border border-[#444] text-[#A3A3A3] text-[10px] uppercase tracking-widest font-bold"
                  title="Não estás a coopetir nesta edição da Growth League. Continuas a registar atividade para o teu histórico de carreira."
                  data-testid="bench-badge"
                >
                  Fora da edição actual
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <MeetingCountdown />

      <LegacyCard />

      <MissionViaPanel player={player} onSaved={load} />

      <ValuePropositionPanel player={player} onSaved={load} />

      <ContactInfoPanel player={player} onSaved={load} />

      <IdealPessoalPanel />

      <div id="gps" className="scroll-mt-20">
        <GPSPanel />
      </div>

      <MyMentorsPanel />

      <GPSYtdReport />

      <SocialLinksPanel player={player} onSaved={load} />

      <LeagueNetworkPanel currentPlayerId={player.id} />

      <InvitePanel />

      <FamilyTreePanel />

      <PushToggle />
      <InstallAppButton />

      <ConversionFunnel stats={stats} />

      {player.personality_description && (
        <div className="kw-card p-5" data-testid="mbti-description">
          <div className="kw-overline mb-2">Sobre o teu perfil {player.mbti} · {player.personality_label}</div>
          <p className="text-[#A3A3A3] leading-relaxed">{player.personality_description}</p>
        </div>
      )}

      <div className="grid md:grid-cols-4 gap-4">
        <StatCard icon={Trophy} label="Pontos Totais" value={Math.round(stats.total_points)} accent />
        <StatCard icon={TrendingUp} label="Vendas" value={stats.totals.vendas} />
        <StatCard icon={Share2} label="Pontos Sociais" value={Math.round(stats.social_points || 0)} hint={`${stats.totals.social_posts || 0} posts · ${stats.totals.humanized_videos || 0} vídeos`} />
        <StatCard icon={AlertTriangle} label="Faltas" value={stats.absences} danger={stats.absences >= 3} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="kw-card p-6">
          <div className="kw-overline mb-3">Saldo presença</div>
          <div className="font-display text-4xl font-black">{stats.presence_balance}</div>
          <p className="text-[#737373] text-sm mt-2">Saldo inicial 100. À 3.ª falta passa para 0.</p>
        </div>
        <div className="kw-card p-6">
          <div className="kw-overline mb-3">Estado MVP</div>
          <div className={`font-display text-3xl font-black uppercase ${stats.mvp_eligible ? "text-[#00FF88]" : "text-[#FF6B6B]"}`}>
            {stats.mvp_eligible ? "Elegível" : "Não elegível"}
          </div>
          {stats.expelled && <p className="text-[#FF6B6B] text-sm mt-2">Estado: Excluído da edição.</p>}
        </div>
      </div>

      <CareerPanel />

      <div className="kw-card p-6">
        <div className="flex items-center justify-between mb-4 border-b border-[#222] pb-3">
          <div>
            <div className="kw-overline">Tendência</div>
            <h2 className="font-display text-2xl font-black uppercase">A Minha Evolução</h2>
          </div>
          <LineIcon className="w-5 h-5 text-[#E50000]" />
        </div>
        <PersonalEvolution weekly={evo?.weekly || []} height={260} />
      </div>

      <div className="kw-card overflow-x-auto">
        <div className="p-6 border-b border-[#222]">
          <div className="kw-overline">Histórico</div>
          <h2 className="font-display text-2xl font-black uppercase">Os meus registos</h2>
        </div>
        <table className="w-full text-left text-sm">
          <thead className="bg-[#0A0A0A] border-b border-[#222]">
            <tr className="kw-overline">
              <th className="p-3">Semana</th>
              <th className="p-3 text-right" title="Contactos">C</th>
              <th className="p-3 text-right" title="Reuniões">R</th>
              <th className="p-3 text-right" title="Angariações">A</th>
              <th className="p-3 text-right" title="Vendas">V</th>
              <th className="p-3 text-right" title="Posts redes sociais">P</th>
              <th className="p-3 text-right" title="Vídeos humanizados">Vid</th>
              <th className="p-3 text-right" title="Formações">F</th>
              <th className="p-3 text-right">Pontos</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && <tr><td colSpan="9" className="p-6 text-center text-[#737373]">Sem registos.</td></tr>}
            {entries.map((e) => (
              <tr key={e.id} className={`border-b border-[#1A1A1A] ${e.reporting === "nao_reportou" ? "opacity-60" : ""}`}>
                <td className="p-3">{e.week_number}</td>
                <td className="p-3 text-right">{e.contactos}</td>
                <td className="p-3 text-right">{e.reunioes}</td>
                <td className="p-3 text-right">{e.angariacoes}</td>
                <td className="p-3 text-right">{e.vendas}</td>
                <td className="p-3 text-right">
                  {e.social_post_url ? (
                    <a href={e.social_post_url} target="_blank" rel="noopener noreferrer" className="text-[#E50000] hover:underline" title="Abrir post">
                      {e.social_posts || 0}
                    </a>
                  ) : (e.social_posts || 0)}
                </td>
                <td className="p-3 text-right">
                  {e.humanized_video_url ? (
                    <a href={e.humanized_video_url} target="_blank" rel="noopener noreferrer" className="text-[#E50000] hover:underline" title="Abrir vídeo">
                      {e.humanized_videos || 0}
                    </a>
                  ) : (e.humanized_videos || 0)}
                </td>
                <td className="p-3 text-right">{e.formacoes || 0}</td>
                <td className="p-3 text-right font-display font-black">{Math.round(e.points)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, danger, hint }) {
  return (
    <div className={`kw-card p-5 ${danger ? "border-[#E50000]/60" : ""}`}>
      <div className="flex items-center justify-between mb-3">
        <Icon className={`w-5 h-5 ${accent ? "text-[#E50000]" : "text-[#A3A3A3]"}`} />
        <div className="kw-overline">{label}</div>
      </div>
      <div className="font-display text-4xl font-black">{value}</div>
      {hint && <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">{hint}</div>}
    </div>
  );
}


export function MissionViaPanel({ player, onSaved, endpoint = "/my-profile", viaImportEndpoint }) {
  const [editing, setEditing] = useState(false);
  const [mission, setMission] = useState(player.mission || "");
  const [vision, setVision] = useState(player.vision || "");
  const [principles, setPrinciples] = useState(player.principles || "");
  const [via, setVia] = useState([...(player.via_top5 || []), "", "", "", "", ""].slice(0, 5));
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  // Privacy: when true, mission/vision/principles are visible to Coach (same club) + Admin.
  // When false (default), only the consultant himself sees them.
  const [share, setShare] = useState(Boolean(player.share_identity_with_coach));
  const [savingShare, setSavingShare] = useState(false);
  const fileRef = React.useRef(null);
  const importUrl = viaImportEndpoint || `/players/${player.id}/via-import`;
  const mbtiCode = player.mbti || player.coach_mbti;

  const togglePrivacy = async () => {
    const next = !share;
    setSavingShare(true);
    try {
      await api.put("/my-identity-privacy", { share_identity_with_coach: next });
      setShare(next);
      toast.success(next ? "Visibilidade ativada para Secretário e Admin" : "Campos voltaram a privado");
      onSaved && onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    setSavingShare(false);
  };

  const importPdf = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(importUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newVia = [...data.via_top5, "", "", "", "", ""].slice(0, 5);
      setVia(newVia);
      toast.success(`${data.via_top5.length} forças extraídas do relatório`);
      setEditing(true);
    } catch (err) { toast.error(formatApiError(err)); }
    setImporting(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(endpoint, {
        mission,
        vision,
        principles,
        via_top5: via.filter(Boolean),
      });
      toast.success("Perfil atualizado");
      setEditing(false);
      onSaved && onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="mission-via-panel">
      <div className="kw-card p-4 md:col-span-2 flex items-center gap-3" data-testid="identity-privacy-bar">
        {share ? (
          <Eye className="w-5 h-5 text-[#22C55E] flex-shrink-0" />
        ) : (
          <Lock className="w-5 h-5 text-[#737373] flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="kw-overline text-[10px]">Privacidade — Missão, Visão e Princípios</div>
          <p className="text-xs text-[#A3A3A3] leading-snug mt-1">
            {share
              ? "Partilhado: o teu Secretário (do mesmo clube) e Admin podem ver os teus textos. Os pontos de bónus (+3 por campo) contam sempre, partilhes ou não."
              : "Privado: só tu vês os teus textos. Os pontos de bónus (+3 por campo) contam sempre."}
          </p>
        </div>
        <button
          onClick={togglePrivacy}
          disabled={savingShare}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border transition-colors flex-shrink-0 ${
            share
              ? "border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E]/10"
              : "border-[#444] text-[#A3A3A3] hover:border-white hover:text-white"
          }`}
          data-testid="identity-privacy-toggle">
          {share ? <><EyeOff className="w-3 h-3" /> Tornar Privado</> : <><Eye className="w-3 h-3" /> Partilhar</>}
        </button>
      </div>

      <div className="kw-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Target className="w-5 h-5 text-[#E50000]" />
            <div className="kw-overline">A minha missão</div>
          </div>
          {!editing && (
            <button onClick={() => setEditing(true)} className="text-xs uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white" data-testid="mission-edit-btn">
              Editar
            </button>
          )}
        </div>
        {editing ? (
          <textarea className="kw-input min-h-[120px]" value={mission}
            onChange={(e) => setMission(e.target.value)} maxLength={500}
            placeholder="A minha missão é…" data-testid="mission-textarea" />
        ) : (
          <p className="text-[#A3A3A3] text-sm leading-relaxed whitespace-pre-wrap min-h-[80px]">
            {player.mission || <span className="italic text-[#737373]">Define a tua missão pessoal — clica em Editar.</span>}
          </p>
        )}
      </div>

      <div className="kw-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <Eye className="w-5 h-5 text-[#E50000]" />
          <div className="kw-overline">A minha visão</div>
        </div>
        {editing ? (
          <textarea className="kw-input min-h-[120px]" value={vision}
            onChange={(e) => setVision(e.target.value)} maxLength={500}
            placeholder="Daqui a 3 anos vejo-me…" data-testid="vision-textarea" />
        ) : (
          <p className="text-[#A3A3A3] text-sm leading-relaxed whitespace-pre-wrap min-h-[80px]" data-testid="vision-text">
            {player.vision || <span className="italic text-[#737373]">Define a tua visão de futuro — clica em Editar acima.</span>}
          </p>
        )}
      </div>

      <div className="kw-card p-5 md:col-span-2">
        <div className="flex items-center gap-2 mb-3">
          <Compass className="w-5 h-5 text-[#E50000]" />
          <div className="kw-overline">Os meus princípios</div>
        </div>
        {editing ? (
          <textarea className="kw-input min-h-[100px]" value={principles}
            onChange={(e) => setPrinciples(e.target.value)} maxLength={600}
            placeholder="Os princípios que guiam as minhas decisões…" data-testid="principles-textarea" />
        ) : (
          <p className="text-[#A3A3A3] text-sm leading-relaxed whitespace-pre-wrap min-h-[60px]" data-testid="principles-text">
            {player.principles || <span className="italic text-[#737373]">Define os princípios pelos quais te guias — clica em Editar acima.</span>}
          </p>
        )}
      </div>

      <div className="kw-card p-5 md:col-span-2">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-[#E50000]" />
            <div className="kw-overline">Top 5 Forças VIA</div>
          </div>
          <div className="flex gap-3 items-center flex-wrap">
            <a href="https://www.viacharacter.org/account/register" target="_blank" rel="noopener noreferrer"
              className="text-xs uppercase tracking-widest font-bold text-[#E50000] hover:text-white inline-flex items-center gap-1"
              data-testid="via-test-link"
              title="Faz o teste VIA Character (gratuito, ~15 min)">
              <ExternalLink className="w-3 h-3" /> Fazer Teste VIA Me
            </a>
            <input ref={fileRef} type="file" accept="application/pdf" className="hidden"
              onChange={(e) => importPdf(e.target.files?.[0])} data-testid="via-pdf-input" />
            <button onClick={() => fileRef.current?.click()} disabled={importing}
              className="text-xs uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white inline-flex items-center gap-1"
              data-testid="via-import-btn"
              title="Importar PDF do relatório VIA-IS — extração automática">
              <FileUp className="w-3 h-3" /> {importing ? "A extrair…" : "Importar PDF"}
            </button>
          </div>
        </div>
        {editing ? (
          <>
            {suggestedViaFor(mbtiCode).length > 0 && (
              <div className="mb-3 p-3 border border-[#E50000]/30 bg-[#E50000]/5">
                <div className="kw-overline mb-2 text-[#E50000]">Sugeridas pelo teu {mbtiCode}</div>
                <div className="flex flex-wrap gap-1.5">
                  {suggestedViaFor(mbtiCode).map((s) => {
                    const already = via.includes(s);
                    return (
                      <button key={s} type="button" disabled={already}
                        onClick={() => {
                          const emptyIdx = via.findIndex((x) => !x);
                          if (emptyIdx === -1) return;
                          const v = [...via]; v[emptyIdx] = s; setVia(v);
                        }}
                        className={`text-[11px] uppercase tracking-widest font-bold px-2 py-1 border transition-colors ${
                          already ? "border-[#333] text-[#555] line-through" : "border-[#E50000]/40 text-white hover:bg-[#E50000] hover:border-[#E50000]"
                        }`}
                        data-testid={`via-suggest-${s}`}>
                        + {s}
                      </button>
                    );
                  })}
                </div>
                <div className="text-[11px] text-[#737373] mt-2">Toca numa sugestão para adicionar ao próximo slot livre. Estas são forças tipicamente alinhadas com o perfil MBTI — usa o teu teste VIA-IS para confirmar.</div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[0,1,2,3,4].map((i) => {
                const others = via.filter((_, idx) => idx !== i).filter(Boolean);
                return (
                  <select key={i} className="kw-input !py-1.5 text-sm"
                    value={via[i] || ""}
                    onChange={(e) => { const v = [...via]; v[i] = e.target.value; setVia(v); }}
                    data-testid={`my-via-${i}`}>
                    <option value="">— #{i + 1} —</option>
                    {Object.entries(VIA_STRENGTHS).map(([virt, items]) => (
                      <optgroup key={virt} label={virt}>
                        {items.map((s) => <option key={s} value={s} disabled={others.includes(s)}>{s}</option>)}
                      </optgroup>
                    ))}
                  </select>
                );
              })}
            </div>
          </>
        ) : player.via_top5 && player.via_top5.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {player.via_top5.map((v, i) => (
              <span key={v} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold border border-[#E50000]/40 bg-[#E50000]/10 text-white">
                <span className="font-display text-[#E50000]">{i + 1}.</span> {v}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm italic text-[#737373]">Adiciona as tuas 5 forças mais relevantes do teste VIA-IS — clica em Editar acima.</p>
        )}
      </div>

      {editing && (
        <div className="md:col-span-2 flex justify-end gap-2">
          <button onClick={() => {
              setMission(player.mission || "");
              setVision(player.vision || "");
              setPrinciples(player.principles || "");
              setVia([...(player.via_top5 || []), "", "", "", "", ""].slice(0, 5));
              setEditing(false);
            }} className="kw-btn-secondary">Cancelar</button>
          <button onClick={save} disabled={saving} className="kw-btn-primary inline-flex items-center gap-2" data-testid="mission-save-btn">
            <Save className="w-4 h-4" /> {saving ? "A guardar…" : "Guardar"}
          </button>
        </div>
      )}
    </div>
  );
}


export function ValuePropositionPanel({ player, onSaved, endpoint = "/my-profile" }) {
  const [editing, setEditing] = useState(false);
  const [vk, setVk] = useState(player.value_knowledge || "");
  const [vp, setVp] = useState(player.value_process || "");
  const [vr, setVr] = useState(player.value_relationship || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(endpoint, {
        value_knowledge: vk,
        value_process: vp,
        value_relationship: vr,
      });
      toast.success("Proposta de valor atualizada");
      setEditing(false);
      onSaved && onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const cards = [
    { key: "k", icon: Briefcase, label: "Conhecimento", val: player.value_knowledge, setter: setVk, state: vk,
      placeholder: "Ex: Domínio das estratégias de captação no segmento residencial premium em Cascais." },
    { key: "p", icon: Workflow, label: "Processo", val: player.value_process, setter: setVp, state: vp,
      placeholder: "Ex: Garanto follow-up estruturado em 48h e visitas guiadas com dossier técnico completo." },
    { key: "r", icon: HeartHandshake, label: "Relação", val: player.value_relationship, setter: setVr, state: vr,
      placeholder: "Ex: Acompanho cada cliente como se fosse o único — disponibilidade, escuta e clareza." },
  ];

  return (
    <div className="kw-card p-6" data-testid="value-proposition-panel">
      <div className="flex items-center justify-between mb-4 border-b border-[#222] pb-3">
        <div>
          <div className="kw-overline">Posicionamento</div>
          <h2 className="font-display text-2xl font-black uppercase">A Minha Proposta de Valor</h2>
        </div>
        {!editing ? (
          <button onClick={() => setEditing(true)} className="text-xs uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white" data-testid="value-prop-edit-btn">
            Editar
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => {
              setVk(player.value_knowledge || ""); setVp(player.value_process || ""); setVr(player.value_relationship || "");
              setEditing(false);
            }} className="kw-btn-secondary !py-1.5 !px-3 text-xs">Cancelar</button>
            <button onClick={save} disabled={saving} className="kw-btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid="value-prop-save-btn">
              <Save className="w-3 h-3" /> {saving ? "A guardar…" : "Guardar"}
            </button>
          </div>
        )}
      </div>
      <p className="text-xs text-[#A3A3A3] mb-4">
        Define em <strong className="text-white">uma frase</strong> o que te torna distinto nestas 3 dimensões. É a base do teu pitch e do teu posicionamento no mercado.
      </p>
      <div className="grid md:grid-cols-3 gap-4">
        {cards.map(({ key, icon: Icon, label, val, setter, state, placeholder }) => (
          <div key={key} className="border border-[#222] p-4 bg-[#0A0A0A]">
            <div className="flex items-center gap-2 mb-3">
              <Icon className="w-4 h-4 text-[#E50000]" />
              <div className="kw-overline">{label}</div>
            </div>
            {editing ? (
              <textarea className="kw-input min-h-[88px] text-sm" value={state}
                onChange={(e) => setter(e.target.value)} maxLength={240}
                placeholder={placeholder} data-testid={`value-prop-${key}-textarea`} />
            ) : (
              <p className="text-sm text-[#A3A3A3] leading-relaxed min-h-[60px]" data-testid={`value-prop-${key}`}>
                {val || <span className="italic text-[#555]">{placeholder}</span>}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ConversionFunnel({ stats }) {
  if (!stats?.totals) return null;
  const c = stats.totals.contactos || 0;
  const r = stats.totals.reunioes || 0;
  const a = stats.totals.angariacoes || 0;
  const v = stats.totals.vendas || 0;
  const mtg = stats.meeting_rate || 0;
  const lst = stats.listing_rate || 0;
  const sale = stats.sale_rate || 0;
  const weighted = stats.weighted_conversion || 0;

  const maxAbs = Math.max(c, 1);
  const steps = [
    { label: "Contactos", val: c, pct: 100, color: "#E50000" },
    { label: "Reuniões", val: r, pct: (r / maxAbs) * 100, color: "#FF1A1A" },
    { label: "Angariações", val: a, pct: (a / maxAbs) * 100, color: "#FF4D4D" },
    { label: "Vendas", val: v, pct: (v / maxAbs) * 100, color: "#FF8080" },
  ];

  return (
    <div className="kw-card p-6" data-testid="conversion-funnel">
      <div className="flex items-center justify-between mb-4 border-b border-[#222] pb-3">
        <div>
          <div className="kw-overline">Eficiência</div>
          <h2 className="font-display text-2xl font-black uppercase">Funil de Conversão</h2>
        </div>
        <Filter className="w-5 h-5 text-[#E50000]" />
      </div>
      <div className="grid md:grid-cols-3 gap-4 mb-5">
        <FunnelRate label="R / C" pct={mtg} hint="Reuniões por contacto · 20%" />
        <FunnelRate label="A / R" pct={lst} hint="Angariações por reunião · 60%" highlight />
        <FunnelRate label="V / A" pct={sale} hint="Vendas por angariação · 20%" />
      </div>
      <div className="border-t border-[#222] pt-4 mb-5 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kw-overline">Taxa de Conversão Ponderada</div>
          <div className="text-xs text-[#737373] mt-1">Critério oficial de desempate · 20%·R/C + 60%·A/R + 20%·V/A</div>
        </div>
        <div className="font-display text-5xl font-black text-[#E50000]" data-testid="weighted-conversion-value">
          {(weighted * 100).toFixed(1)}%
        </div>
      </div>
      <div className="space-y-3">
        {steps.map((s) => (
          <div key={s.label}>
            <div className="flex items-center justify-between mb-1">
              <span className="kw-overline">{s.label}</span>
              <span className="font-display text-sm font-black">{s.val}</span>
            </div>
            <div className="kw-funnel-bar">
              <span style={{ width: `${Math.min(100, s.pct)}%`, background: s.color }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FunnelRate({ label, pct, hint, highlight }) {
  return (
    <div className={`border p-4 bg-[#0A0A0A] ${highlight ? "border-[#E50000]/50" : "border-[#222]"}`}>
      <div className="kw-overline mb-2">{label}</div>
      <div className={`font-display text-3xl font-black ${highlight ? "text-[#E50000]" : ""}`}>
        {(pct * 100).toFixed(1)}%
      </div>
      <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">{hint}</div>
    </div>
  );
}

function ContactInfoPanel({ player, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [phone, setPhone] = useState(player.phone || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/my-profile", { phone });
      toast.success("Telemóvel atualizado");
      setEditing(false);
      onSaved && onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  return (
    <div className="kw-card p-5" data-testid="contact-info-panel">
      <div className="flex items-center justify-between mb-3 border-b border-[#222] pb-2">
        <div>
          <div className="kw-overline">Contacto</div>
          <h2 className="font-display text-xl font-black uppercase">Telemóvel</h2>
        </div>
        {!editing ? (
          <button onClick={() => setEditing(true)} className="text-xs uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white" data-testid="contact-edit-btn">
            Editar
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => { setPhone(player.phone || ""); setEditing(false); }} className="kw-btn-secondary !py-1.5 !px-3 text-xs">Cancelar</button>
            <button onClick={save} disabled={saving} className="kw-btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid="contact-save-btn">
              <Save className="w-3 h-3" /> {saving ? "A guardar…" : "Guardar"}
            </button>
          </div>
        )}
      </div>
      {editing ? (
        <input className="kw-input" placeholder="+351 ..." value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="contact-phone-input" />
      ) : (
        <div className="text-sm text-[#A3A3A3]">
          {player.phone ? (
            <a href={`tel:${player.phone.replace(/\s+/g, "")}`} className="hover:text-white" data-testid="contact-phone-display">{player.phone}</a>
          ) : <span className="italic">Sem telemóvel definido — adiciona o teu para aparecer no diretório.</span>}
        </div>
      )}
    </div>
  );
}

function SocialLinksPanel({ player, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [linkedin, setLinkedin] = useState(player.linkedin_url || "");
  const [instagram, setInstagram] = useState(player.instagram_url || "");
  const [facebook, setFacebook] = useState(player.facebook_url || "");
  const [tiktok, setTiktok] = useState(player.tiktok_url || "");
  const [youtube, setYoutube] = useState(player.youtube_url || "");
  const [saving, setSaving] = useState(false);

  const fields = [
    { key: "linkedin", icon: Linkedin, label: "LinkedIn", color: "#0A66C2", val: linkedin, setter: setLinkedin, current: player.linkedin_url, placeholder: "https://linkedin.com/in/o-teu-perfil" },
    { key: "instagram", icon: Instagram, label: "Instagram", color: "#E1306C", val: instagram, setter: setInstagram, current: player.instagram_url, placeholder: "https://instagram.com/utilizador" },
    { key: "facebook", icon: Facebook, label: "Facebook", color: "#1877F2", val: facebook, setter: setFacebook, current: player.facebook_url, placeholder: "https://facebook.com/utilizador" },
    { key: "tiktok", icon: FileText, label: "TikTok", color: "#FF0050", val: tiktok, setter: setTiktok, current: player.tiktok_url, placeholder: "https://tiktok.com/@utilizador" },
    { key: "youtube", icon: Youtube, label: "YouTube", color: "#FF0000", val: youtube, setter: setYoutube, current: player.youtube_url, placeholder: "https://youtube.com/@canal" },
  ];

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/my-profile", {
        linkedin_url: linkedin,
        instagram_url: instagram,
        facebook_url: facebook,
        tiktok_url: tiktok,
        youtube_url: youtube,
      });
      toast.success("Redes sociais atualizadas");
      setEditing(false);
      onSaved && onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const hasAny = fields.some((f) => f.current);

  return (
    <div className="kw-card p-6" data-testid="social-links-panel">
      <div className="flex items-center justify-between mb-4 border-b border-[#222] pb-3">
        <div className="flex items-center gap-2">
          <Share2 className="w-5 h-5 text-[#E50000]" />
          <div>
            <div className="kw-overline">Marca Pessoal</div>
            <h2 className="font-display text-2xl font-black uppercase">As Minhas Redes Sociais</h2>
          </div>
        </div>
        {!editing ? (
          <button onClick={() => setEditing(true)} className="text-xs uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white" data-testid="social-edit-btn">
            Editar
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => {
                setLinkedin(player.linkedin_url || ""); setInstagram(player.instagram_url || "");
                setFacebook(player.facebook_url || ""); setTiktok(player.tiktok_url || "");
                setYoutube(player.youtube_url || ""); setEditing(false);
              }} className="kw-btn-secondary !py-1.5 !px-3 text-xs">Cancelar</button>
            <button onClick={save} disabled={saving} className="kw-btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid="social-save-btn">
              <Save className="w-3 h-3" /> {saving ? "A guardar…" : "Guardar"}
            </button>
          </div>
        )}
      </div>
      <p className="text-xs text-[#A3A3A3] mb-4">
        Partilha os teus perfis públicos. Servem de prova das tuas publicações (<strong className="text-white">+5 pts</strong>/post · <strong className="text-white">+10 pts</strong>/vídeo) e serão validadas manualmente pelo teu coach.
      </p>

      {editing ? (
        <div className="grid md:grid-cols-2 gap-3">
          {fields.map(({ key, icon: Icon, label, color, val, setter, placeholder }) => (
            <div key={key} className="flex items-center gap-2 border border-[#222] bg-[#0A0A0A] px-3 py-2">
              <Icon className="w-4 h-4 flex-shrink-0" style={{ color }} />
              <input type="url" className="kw-input !border-0 !bg-transparent !p-0 text-sm flex-1"
                value={val} onChange={(e) => setter(e.target.value)}
                placeholder={placeholder} data-testid={`social-${key}-input`} />
            </div>
          ))}
        </div>
      ) : hasAny ? (
        <div className="flex flex-wrap gap-2">
          {fields.filter((f) => f.current).map(({ key, icon: Icon, label, color, current }) => (
            <a key={key} href={current} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 border border-[#222] hover:border-[#E50000] text-sm transition-colors"
              data-testid={`social-${key}-link`}>
              <Icon className="w-4 h-4" style={{ color }} />
              <span className="font-semibold">{label}</span>
              <ExternalLink className="w-3 h-3 text-[#737373]" />
            </a>
          ))}
        </div>
      ) : (
        <p className="text-sm italic text-[#737373]">Adiciona os teus perfis sociais — clica em Editar acima. São opcionais mas ajudam o secretário a validar os teus posts e vídeos.</p>
      )}
    </div>
  );
}

const NETWORK_DEFS = [
  { key: "linkedin", urlField: "linkedin_url", icon: Linkedin, label: "LinkedIn", color: "#0A66C2" },
  { key: "instagram", urlField: "instagram_url", icon: Instagram, label: "Instagram", color: "#E1306C" },
  { key: "facebook", urlField: "facebook_url", icon: Facebook, label: "Facebook", color: "#1877F2" },
  { key: "tiktok", urlField: "tiktok_url", icon: FileText, label: "TikTok", color: "#FF0050" },
  { key: "youtube", urlField: "youtube_url", icon: Youtube, label: "YouTube", color: "#FF0000" },
];

function LeagueNetworkPanel({ currentPlayerId }) {
  const [colleagues, setColleagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clubFilter, setClubFilter] = useState("all");
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    api.get("/players/social").then((r) => {
      setColleagues(r.data.filter((p) => p.id !== currentPlayerId));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [currentPlayerId]);

  const filtered = colleagues.filter((c) => clubFilter === "all" || c.club === clubFilter);

  const followAll = (network) => {
    const urls = filtered.map((c) => c[network.urlField]).filter(Boolean);
    if (urls.length === 0) {
      toast.info(`Nenhum colega tem ${network.label} configurado neste filtro.`);
      return;
    }
    if (urls.length > 12) {
      const ok = window.confirm(`Vais abrir ${urls.length} separadores do ${network.label} de uma vez. O teu browser pode bloquear alguns popups — confirma na barra de endereço. Continuar?`);
      if (!ok) return;
    }
    urls.forEach((url, i) => {
      setTimeout(() => window.open(url, "_blank", "noopener,noreferrer"), i * 120);
    });
    toast.success(`A abrir ${urls.length} perfis de ${network.label}…`);
  };

  const counts = NETWORK_DEFS.reduce((acc, n) => {
    acc[n.key] = filtered.filter((c) => c[n.urlField]).length;
    return acc;
  }, {});

  return (
    <div className="kw-card p-6" data-testid="league-network-panel">
      <div className="flex items-center justify-between mb-4 border-b border-[#222] pb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-[#E50000]" />
          <div>
            <div className="kw-overline">Marca pessoal · Liga</div>
            <h2 className="font-display text-2xl font-black uppercase">Rede da Liga</h2>
          </div>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="text-xs uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white inline-flex items-center gap-1" data-testid="network-toggle">
          {expanded ? <>Recolher <ChevronUp className="w-3 h-3" /></> : <>Expandir <ChevronDown className="w-3 h-3" /></>}
        </button>
      </div>
      <p className="text-xs text-[#A3A3A3] mb-4">
        Segue os teus colegas da liga nas redes sociais. Quanto mais colegas te seguirem e tu seguires, mais alcance orgânico têm as publicações de cada um — beneficia toda a marca KW Lead.
      </p>

      {expanded && (
        <>
          {/* Follow-all buttons */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
            {NETWORK_DEFS.map((n) => (
              <button key={n.key} onClick={() => followAll(n)}
                disabled={counts[n.key] === 0}
                className="border border-[#222] hover:border-[#E50000] disabled:opacity-40 disabled:cursor-not-allowed p-3 text-left transition-colors"
                style={{ borderColor: counts[n.key] > 0 ? `${n.color}50` : undefined }}
                data-testid={`follow-all-${n.key}`}>
                <n.icon className="w-4 h-4 mb-2" style={{ color: n.color }} />
                <div className="text-xs font-bold uppercase tracking-widest">{n.label}</div>
                <div className="text-[10px] text-[#737373] mt-1">Seguir {counts[n.key]} colega{counts[n.key] === 1 ? "" : "s"}</div>
              </button>
            ))}
          </div>

          {/* Club filter */}
          <div className="flex gap-2 items-center mb-3 flex-wrap">
            <Filter className="w-3 h-3 text-[#737373]" />
            <button onClick={() => setClubFilter("all")}
              className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1 border ${clubFilter === "all" ? "border-[#E50000] text-[#E50000]" : "border-[#222] text-[#A3A3A3]"}`}
              data-testid="network-filter-all">
              Todos ({colleagues.length})
            </button>
            {Object.keys(CLUB_NETWORK_COLORS).map((c) => {
              const n = colleagues.filter((x) => x.club === c).length;
              return (
                <button key={c} onClick={() => setClubFilter(c)} disabled={n === 0}
                  className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1 border disabled:opacity-30 ${clubFilter === c ? "border-[#E50000] text-[#E50000]" : "border-[#222] text-[#A3A3A3]"}`}
                  data-testid={`network-filter-${c}`}>
                  {c} ({n})
                </button>
              );
            })}
          </div>

          {/* Colleagues list */}
          {loading ? (
            <p className="text-sm text-[#737373] text-center py-6">A carregar rede…</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm italic text-[#737373] text-center py-6">
              {colleagues.length === 0
                ? "Os teus colegas ainda não preencheram as redes sociais. Sê o primeiro!"
                : "Nenhum colega neste clube. Tenta outro filtro."}
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2">
              {filtered.map((c) => (
                <div key={c.id} className="border border-[#222] p-3 flex items-center gap-3" data-testid={`network-row-${c.id}`}>
                  <div className="flex-shrink-0">
                    {c.photo_url ? (
                      <img src={c.photo_url} alt={c.name} className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center text-xs font-bold text-[#737373]">
                        {(c.name || "?").slice(0, 2).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate text-sm">{c.name}</div>
                    <div className="text-[10px] text-[#737373] uppercase tracking-widest">
                      {c.club ? <span style={{ color: CLUB_NETWORK_COLORS[c.club] }}>{c.club}</span> : "—"}
                      {c.mbti && <span> · {c.mbti}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    {NETWORK_DEFS.map((n) => c[n.urlField] ? (
                      <a key={n.key} href={c[n.urlField]} target="_blank" rel="noopener noreferrer"
                        className="hover:opacity-70" title={`${n.label} · ${c.name}`}
                        data-testid={`network-${n.key}-${c.id}`}>
                        <n.icon className="w-4 h-4" style={{ color: n.color }} />
                      </a>
                    ) : null)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const CLUB_NETWORK_COLORS = {
  Analistas: "#9333EA",
  Diplomatas: "#10B981",
  Sentinelas: "#3B82F6",
  Exploradores: "#F59E0B",
};
